import type { DragHandleProps } from "@repo/ui/components/kibo-ui/list";
import { Badge } from "@repo/ui/components/ui/badge";
import { Button } from "@repo/ui/components/ui/button";
import { cn } from "@repo/ui/lib/utils";
import { Box, Copy, GripVertical, Trash2 } from "lucide-react";
import { useMemo } from "react";
import type { ActionFormValues } from "@/features/catalog/forms/use-plan-definition-form";
import { markdownLine } from "@/shared/lib/markdown-line";
import { qualifiedProcedureTitle } from "@/shared/lib/procedure-title";
import { SplitProcedureButton } from "@/shared/timeline";
import {
  findProtocolBuilderProcedure,
  type ProtocolBuilderProcedure,
  protocolBuilderParameterBadges,
} from "./protocol-builder-definition";

/**
 * Resolve the display title for a ProcedureCard using the priority chain:
 *   1. action.title (explicit form value)
 *   2. resolved ActivityDefinition title
 *   3. resolved ActivityDefinition name
 *   4. positional fallback (`Procedure ${index + 1}`)
 */
export function resolveProcedureCardTitle(
  actionTitle: string | undefined,
  definition: { title?: string; name?: string } | undefined,
  index: number,
): string {
  if (actionTitle) return actionTitle;
  if (definition?.title) return definition.title;
  if (definition?.name) return definition.name;
  return `Procedure ${index + 1}`;
}

interface ProcedureCardProps {
  action: ActionFormValues;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
  /** Present only on a procedure that shares its step. */
  onSplit?: () => void;
  dragHandleProps?: DragHandleProps;
  hasResourceRequirements?: boolean;
  activityDefinitions?: ProtocolBuilderProcedure[];
  showDescription?: boolean;
  requiredByExamNames?: string[];
}

export function ProcedureCard({
  action,
  index,
  isSelected,
  onSelect,
  onDuplicate,
  onRemove,
  onSplit,
  dragHandleProps,
  hasResourceRequirements,
  activityDefinitions = [],
  showDescription = true,
  requiredByExamNames = [],
}: ProcedureCardProps) {
  const adForAction = useMemo(
    () => findProtocolBuilderProcedure(action.definitionCanonical, activityDefinitions),
    [action.definitionCanonical, activityDefinitions],
  );

  const parameterBadges = useMemo(() => {
    if (!adForAction) return [];
    const values = action.parameterValues;
    if (values.length === 0) return [];
    return protocolBuilderParameterBadges(adForAction, values);
  }, [adForAction, action.parameterValues]);

  const title = qualifiedProcedureTitle(resolveProcedureCardTitle(action.title, adForAction, index), action.qualifier);

  const catalogType = useMemo(() => {
    if (!adForAction) return null;
    return adForAction.kind;
  }, [adForAction]);

  return (
    <div
      data-procedure-card
      className={cn(
        "flex min-w-0 items-stretch gap-1 rounded-md border bg-background p-2 text-sm transition-colors",
        isSelected && "ring-2 ring-primary",
      )}
    >
      {dragHandleProps && (
        <div
          ref={dragHandleProps.ref}
          className="cursor-grab text-muted-foreground hover:text-foreground self-stretch flex items-center px-1 -my-2 -ml-2 rounded-l-md hover:bg-muted/50"
        >
          <GripVertical className="size-4" />
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-w-0 items-start justify-between gap-1">
          <button
            type="button"
            className="flex min-w-0 flex-1 cursor-pointer flex-col gap-1 text-left"
            onClick={onSelect}
          >
            <div className="flex items-center gap-2">
              <span className="break-words min-w-0">{title}</span>
              {catalogType && (
                <Badge variant="default" className="text-xs font-normal px-1.5 py-0 shrink-0">
                  {catalogType}
                </Badge>
              )}

              {hasResourceRequirements && (
                <Box className="size-3 text-muted-foreground shrink-0" aria-label="Requires resources" />
              )}
            </div>
            {showDescription && action.description && (
              <div className="text-xs text-muted-foreground truncate">{markdownLine(action.description)}</div>
            )}
            {requiredByExamNames.length > 0 && (
              <p className="text-xs text-muted-foreground">Required by {requiredByExamNames.join(", ")}</p>
            )}
          </button>
          <div className="flex items-center gap-0.5 shrink-0 self-start">
            {onSplit && <SplitProcedureButton procedureName={title} className="size-6" onClick={onSplit} />}
            <Button variant="ghost" size="icon" className="size-6" onClick={onDuplicate}>
              <Copy className="size-3" />
            </Button>
            <Button
              aria-label="Remove procedure"
              variant="ghost"
              size="icon"
              className="size-6 text-destructive hover:text-destructive"
              onClick={onRemove}
            >
              <Trash2 className="size-3" />
            </Button>
          </div>
        </div>
        {!!action.plannedTrials?.length && (
          <Badge variant="secondary" className="w-fit text-xs font-normal px-1.5 py-0">
            {action.plannedTrials.length} planned {action.plannedTrials.length === 1 ? "trial" : "trials"}
          </Badge>
        )}
        {catalogType !== "Treatment" && !!action.observationLevels?.length && (
          <span className="text-xs text-muted-foreground">Available per trial · {action.observationLevels.length}</span>
        )}
        {parameterBadges.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {parameterBadges.map((b) => (
              <Badge key={b.label} variant="secondary" className="text-xs font-normal px-1.5 py-0">
                <span className="text-muted-foreground">{b.label}:</span> {b.display}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
